import type React from 'react';
import { isSkyxHandheldLandscape } from '@core/utils/skyxViewport';

export type SkyxChatPanelLayout = Pick<
  React.CSSProperties,
  | 'width'
  | 'height'
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'maxWidth'
  | 'borderRadius'
  | 'transform'
  | 'margin'
  | 'position'
>;

/** Layout profile — each can be tuned independently in SKYX_NEW_CHAT_PANEL_LAYOUT. */
export type SkyxNewChatPanelProfile =
  | 'desktop'
  | 'mobile'
  | 'tablet'
  | 'tabletLandscape'
  | 'handheldLandscape';

export type SkyxNewChatPanelPositionConfig = {
  width: string;
  maxWidth?: string;
  height: string;
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  transform?: string;
  margin?: string;
  borderRadius: string;
};

/**
 * Desktop chat footprint — tablet profiles scale the same viewport % (300px @ 1440px wide).
 * Height uses 70dvh with the same chrome offsets; panel floats above the bet area via bottom.
 */
const SKYX_CHAT_DESKTOP_REF_VIEWPORT_WIDTH_PX = 1440;
const SKYX_CHAT_DESKTOP_WIDTH_PX = 300;
const SKYX_CHAT_DESKTOP_WIDTH_VW =
  (SKYX_CHAT_DESKTOP_WIDTH_PX / SKYX_CHAT_DESKTOP_REF_VIEWPORT_WIDTH_PX) * 100;

const SKYX_CHAT_DESKTOP_LIKE_SIZE = {
  width: `clamp(240px, ${SKYX_CHAT_DESKTOP_WIDTH_VW.toFixed(2)}vw, ${SKYX_CHAT_DESKTOP_WIDTH_PX}px)`,
  maxWidth: `${SKYX_CHAT_DESKTOP_WIDTH_PX}px`,
  height: 'calc(70dvh - 140px)',
  bottom: '265px',
  borderRadius: '12px',
} as const satisfies Pick<
  SkyxNewChatPanelPositionConfig,
  'width' | 'maxWidth' | 'height' | 'bottom' | 'borderRadius'
>;

/**
 * Tablet — wider than desktop, right-aligned over the game canvas.
 * (Scoreboard / bets sit below the canvas on tablet, not on the left.)
 */
const SKYX_CHAT_TABLET_REF_VIEWPORT_WIDTH_PX = 1024;
const SKYX_CHAT_TABLET_WIDTH_PX = 600;
const SKYX_CHAT_TABLET_WIDTH_VW =
  (SKYX_CHAT_TABLET_WIDTH_PX / SKYX_CHAT_TABLET_REF_VIEWPORT_WIDTH_PX) * 100;

const SKYX_CHAT_TABLET_PANEL_SIZE = {
  width: `clamp(320px, ${SKYX_CHAT_TABLET_WIDTH_VW.toFixed(2)}vw, 420px)`,
  maxWidth: '500px',
  height: 'calc(62dvh - 10px)',
  bottom: 'clamp(200px, 28dvh, 260px)',
  right: '16px',
  left: 'auto',
  borderRadius: '12px',
} as const satisfies Pick<
  SkyxNewChatPanelPositionConfig,
  'width' | 'maxWidth' | 'height' | 'bottom' | 'right' | 'left' | 'borderRadius'
>;

const SKYX_CHAT_TABLET_LANDSCAPE_PANEL_SIZE = {
  width: 'clamp(340px, 40vw, 440px)',
  maxWidth: '440px',
  height: 'calc(68dvh - 120px)',
  bottom: 'clamp(160px, 22dvh, 220px)',
  right: '12px',
  left: 'auto',
  borderRadius: '12px',
} as const satisfies Pick<
  SkyxNewChatPanelPositionConfig,
  'width' | 'maxWidth' | 'height' | 'bottom' | 'right' | 'left' | 'borderRadius'
>;

/**
 * Sky-x new chat panel position per form factor.
 * Edit the profile you need — mobile, tablet, tablet landscape, handheld landscape, desktop.
 */
export const SKYX_NEW_CHAT_PANEL_LAYOUT: Record<
  SkyxNewChatPanelProfile,
  SkyxNewChatPanelPositionConfig
> = {
  mobile: {
    width: '100%',
    maxWidth: '100%',
    height: '100%',
    top: '0',
    bottom: '0',
    left: '0',
    right: '0',
    borderRadius: '0',
  },
  tablet: {
    ...SKYX_CHAT_TABLET_PANEL_SIZE,
  },
  tabletLandscape: {
    ...SKYX_CHAT_TABLET_LANDSCAPE_PANEL_SIZE,
  },
  handheldLandscape: {
    width: 'min(420px, 44vw)',
    maxWidth: '420px',
    height: '100%',
    top: '0',
    bottom: '0',
    right: '0',
    left: 'auto',
    borderRadius: '8px 0 0 8px',
  },
  desktop: {
    width: `${SKYX_CHAT_DESKTOP_WIDTH_PX}px`,
    maxWidth: `${SKYX_CHAT_DESKTOP_WIDTH_PX}px`,
    height: SKYX_CHAT_DESKTOP_LIKE_SIZE.height,
    bottom: SKYX_CHAT_DESKTOP_LIKE_SIZE.bottom,
    left: 'auto',
    right: 'auto',
    borderRadius: SKYX_CHAT_DESKTOP_LIKE_SIZE.borderRadius,
  },
};

export function resolveSkyxNewChatPanelProfile(
  deviceType: string,
  isLandscape: boolean
): SkyxNewChatPanelProfile {
  if (deviceType === 'desktop') {
    return 'desktop';
  }
  if (isSkyxHandheldLandscape(deviceType, isLandscape)) {
    return 'handheldLandscape';
  }
  if (deviceType === 'mobile') {
    return 'mobile';
  }
  if (deviceType === 'tablet' && isLandscape) {
    return 'tabletLandscape';
  }
  return 'tablet';
}

export type SkyxChatStageBoardAnchor = Pick<
  DOMRect,
  'top' | 'left' | 'width' | 'height' | 'right' | 'bottom'
>;

/** Stage-board shorter than this after rotation → use right-rail fallback layout. */
const SKYX_HANDHELD_LANDSCAPE_MIN_PANEL_SPAN_PX = 180;

function readSkyxViewportHeight(): number {
  if (typeof window === 'undefined') {
    return 520;
  }
  return window.visualViewport?.height ?? window.innerHeight;
}

function readSkyxHeaderOffsetPx(): number {
  if (typeof document === 'undefined') {
    return 42;
  }
  const raw = getComputedStyle(document.documentElement).getPropertyValue(
    '--headerHeightDynamic'
  );
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 42;
}

/** Insets from #stage-board edges — desktop chat tracks the canvas, not viewport px. */
export const SKYX_CHAT_DESKTOP_CANVAS_INSETS = {
  left: 1,
  top: 0,
  bottom: 120,
} as const;

/** Desktop — size + vertical span aligned to game canvas (stable in windowed + fullscreen). */
export function getSkyxNewDesktopAnchoredPanelStyle(
  anchor: SkyxChatStageBoardAnchor,
  position: SkyxNewChatPanelPositionConfig
): SkyxChatPanelLayout {
  const viewportHeight = readSkyxViewportHeight();
  const {
    left: leftInset,
    top: topInset,
    bottom: bottomInset,
  } = SKYX_CHAT_DESKTOP_CANVAS_INSETS;

  return {
    position: 'fixed',
    left: `${Math.round(anchor.left + leftInset)}px`,
    top: `${Math.max(0, anchor.top + topInset)}px`,
    bottom: `${Math.max(0, viewportHeight - anchor.bottom + bottomInset)}px`,
    width: position.width,
    maxWidth: position.maxWidth,
    borderRadius: position.borderRadius,
  };
}

/** Handheld landscape — stretch between stage-board top/bottom (not fixed height px). */
export function getSkyxNewHandheldLandscapeAnchoredPanelStyle(
  anchor: SkyxChatStageBoardAnchor,
  position: SkyxNewChatPanelPositionConfig
): SkyxChatPanelLayout {
  const viewportHeight = readSkyxViewportHeight();
  const panelWidth = Math.min(
    420,
    Math.round(Math.max(anchor.width, 0) * 0.44)
  );
  const span = anchor.bottom - anchor.top;

  if (span < SKYX_HANDHELD_LANDSCAPE_MIN_PANEL_SPAN_PX) {
    return {
      position: 'fixed',
      top: `${readSkyxHeaderOffsetPx()}px`,
      bottom: '0px',
      right: position.right ?? '0',
      left: 'auto',
      width: position.width,
      maxWidth: position.maxWidth,
      borderRadius: position.borderRadius,
    };
  }

  return {
    position: 'fixed',
    top: `${Math.max(0, anchor.top)}px`,
    bottom: `${Math.max(0, viewportHeight - anchor.bottom)}px`,
    left: `${anchor.right - panelWidth}px`,
    right: 'auto',
    width: `${panelWidth}px`,
    maxWidth: position.maxWidth,
    borderRadius: position.borderRadius,
  };
}

/** Fixed panel bounds aligned to #stage-board (portal target is document.body). */
export function getSkyxNewChatPanelAnchoredStyle(
  deviceType: string,
  isLandscape: boolean,
  anchor: SkyxChatStageBoardAnchor
): SkyxChatPanelLayout {
  const profile = resolveSkyxNewChatPanelProfile(deviceType, isLandscape);
  const position = SKYX_NEW_CHAT_PANEL_LAYOUT[profile];

  if (isSkyxNewChatFullCanvasBleed(deviceType, isLandscape)) {
    return {
      position: 'fixed',
      top: `${anchor.top}px`,
      left: `${anchor.left}px`,
      width: `${anchor.width}px`,
      height: `${anchor.height}px`,
      borderRadius: position.borderRadius,
    };
  }

  if (isSkyxHandheldLandscape(deviceType, isLandscape)) {
    return getSkyxNewHandheldLandscapeAnchoredPanelStyle(anchor, position);
  }

  if (deviceType === 'desktop') {
    return getSkyxNewDesktopAnchoredPanelStyle(anchor, position);
  }

  return {
    position: 'fixed',
    top: `${anchor.top}px`,
    left: `${anchor.left}px`,
    width: `${anchor.width}px`,
    height: `${anchor.height}px`,
    borderRadius: position.borderRadius,
  };
}

export function isSkyxNewChatCompactChrome(
  deviceType: string,
  isLandscape: boolean
): boolean {
  return (
    deviceType === 'mobile' || isSkyxHandheldLandscape(deviceType, isLandscape)
  );
}

/** Handheld landscape — emoji picker layout (mobile landscape only). */
export type SkyxNewChatHandheldLandscapeEmojiLayout = {
  /** Emoji rows shown in the grid. */
  visibleRows: number;
  /** Height of one emoji row (px). */
  rowHeightPx: number;
  /** Gap between grid rows (px). */
  rowGapPx: number;
  /**
   * Direct grid height override (px). When set, visibleRows / rowHeightPx are ignored
   * for max-height; use this for fine height tuning.
   */
  gridMaxHeightPx?: number;
  /** Picker container padding on all sides (px). */
  panelPaddingPx: number;
  /** Search field row height including margin below (px). */
  searchRowHeightPx: number;
  /** Space between picker bottom and the composer (px). */
  gapAboveComposerPx: number;
  /** Horizontal inset from composer edges (px). Negative widens past the footer. */
  insetLeftPx: number;
  insetRightPx: number;
  /**
   * Extra vertical offset from the composer top edge (px).
   * Positive = move picker up, negative = move picker down.
   */
  offsetBottomPx: number;
  /** Optional CSS transform, e.g. `translateY(-4px)`. */
  transform?: string;
  /** Emoji columns in the grid. */
  gridColumns: number;
};

/**
 * Mobile landscape emoji picker — edit here to tune position and height.
 * Applies only when `isSkyxHandheldLandscape()` is true.
 */
export const SKYX_NEW_CHAT_HANDHELD_LANDSCAPE_EMOJI_LAYOUT: SkyxNewChatHandheldLandscapeEmojiLayout =
  {
    visibleRows: 2,
    rowHeightPx: 24,
    rowGapPx: 1,
    panelPaddingPx: 8,
    searchRowHeightPx: 34,
    gapAboveComposerPx: 8,
    insetLeftPx: 0,
    insetRightPx: 0,
    offsetBottomPx: -9,
    gridColumns: 7,
  };

function resolveHandheldLandscapeEmojiGridMaxHeightPx(): number {
  const layout = SKYX_NEW_CHAT_HANDHELD_LANDSCAPE_EMOJI_LAYOUT;
  if (layout.gridMaxHeightPx != null) {
    return layout.gridMaxHeightPx;
  }
  const rows = layout.visibleRows;
  return rows * layout.rowHeightPx + (rows - 1) * layout.rowGapPx;
}

/** Emoji grid max height inside the chat footer (px). */
export function getSkyxNewEmojiPickerGridMaxHeightPx(
  deviceType: string,
  isLandscape: boolean
): number {
  if (isSkyxHandheldLandscape(deviceType, isLandscape)) {
    return resolveHandheldLandscapeEmojiGridMaxHeightPx();
  }
  if (deviceType === 'mobile') {
    return 112;
  }
  return 132;
}

/** Full emoji picker height when floated above the composer (handheld landscape). */
export function getSkyxNewHandheldLandscapeEmojiPickerOverlayHeightPx(
  deviceType: string,
  isLandscape: boolean
): number {
  if (!isSkyxHandheldLandscape(deviceType, isLandscape)) {
    return 0;
  }
  const layout = SKYX_NEW_CHAT_HANDHELD_LANDSCAPE_EMOJI_LAYOUT;
  const gridMaxHeight = resolveHandheldLandscapeEmojiGridMaxHeightPx();
  const panelPaddingY = layout.panelPaddingPx * 2;
  return (
    panelPaddingY +
    layout.searchRowHeightPx +
    gridMaxHeight +
    layout.gapAboveComposerPx +
    Math.max(0, layout.offsetBottomPx)
  );
}

/** Absolute position for the handheld-landscape emoji picker above the composer. */
export function getSkyxNewHandheldLandscapeEmojiPickerPositionStyle(): Pick<
  React.CSSProperties,
  | 'position'
  | 'left'
  | 'right'
  | 'bottom'
  | 'marginBottom'
  | 'transform'
  | 'zIndex'
> {
  const layout = SKYX_NEW_CHAT_HANDHELD_LANDSCAPE_EMOJI_LAYOUT;
  const bottom =
    layout.offsetBottomPx === 0
      ? '100%'
      : `calc(100% + ${layout.offsetBottomPx}px)`;

  return {
    position: 'absolute',
    left: layout.insetLeftPx,
    right: layout.insetRightPx,
    bottom,
    marginBottom: `${layout.gapAboveComposerPx}px`,
    transform: layout.transform,
    zIndex: 5,
  };
}

/** Handheld-landscape emoji picker chrome (padding + grid columns). */
export function getSkyxNewHandheldLandscapeEmojiPickerChrome(): {
  panelPaddingPx: number;
  gridColumns: number;
} {
  const layout = SKYX_NEW_CHAT_HANDHELD_LANDSCAPE_EMOJI_LAYOUT;
  return {
    panelPaddingPx: layout.panelPaddingPx,
    gridColumns: layout.gridColumns,
  };
}

/** Chat panel tracks #stage-board rect (desktop + portaled handheld layouts). */
export function isSkyxNewChatUsesStageBoardAnchor(
  deviceType: string,
  isLandscape: boolean
): boolean {
  if (deviceType === 'desktop') {
    return true;
  }
  return isSkyxNewChatPortaledToStageBoard(deviceType, isLandscape);
}

/** Chat mounts inside #stage-board (game canvas), not document.body. */
export function isSkyxNewChatPortaledToStageBoard(
  deviceType: string,
  isLandscape: boolean
): boolean {
  if (deviceType === 'mobile') {
    return true;
  }
  return isSkyxHandheldLandscape(deviceType, isLandscape);
}

/** Mobile portrait — panel fills the entire game canvas. */
export function isSkyxNewChatFullCanvasBleed(
  deviceType: string,
  isLandscape: boolean
): boolean {
  return deviceType === 'mobile' && !isLandscape;
}

/** Read #stage-board bounds for portaled chat anchoring. */
export function readSkyxChatStageBoardAnchor(): SkyxChatStageBoardAnchor | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const board = document.getElementById('stage-board');
  if (!board) {
    return null;
  }
  const rect = board.getBoundingClientRect();
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
    right: rect.right,
    bottom: rect.bottom,
  };
}

/** @deprecated Use isSkyxNewChatPortaledToStageBoard */
export function isSkyxNewChatAnchoredToGameCanvas(
  deviceType: string,
  isLandscape: boolean
): boolean {
  return isSkyxNewChatPortaledToStageBoard(deviceType, isLandscape);
}

export function getSkyxNewChatPanelLayout(
  deviceType: string,
  isLandscape: boolean
): SkyxChatPanelLayout {
  const profile = resolveSkyxNewChatPanelProfile(deviceType, isLandscape);
  const position = SKYX_NEW_CHAT_PANEL_LAYOUT[profile];
  const portaledToStageBoard = isSkyxNewChatPortaledToStageBoard(
    deviceType,
    isLandscape
  );

  if (isSkyxNewChatFullCanvasBleed(deviceType, isLandscape)) {
    return {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      borderRadius: position.borderRadius,
    };
  }

  if (
    portaledToStageBoard &&
    isSkyxHandheldLandscape(deviceType, isLandscape)
  ) {
    return {
      position: 'absolute',
      top: position.top,
      bottom: position.bottom,
      right: position.right,
      left: position.left,
      width: position.width,
      maxWidth: position.maxWidth,
      borderRadius: position.borderRadius,
    };
  }

  return {
    position: 'fixed',
    width: position.width,
    maxWidth: position.maxWidth,
    height: position.height,
    top: position.top,
    bottom: position.bottom,
    left: position.left,
    right: position.right,
    transform: position.transform,
    margin: position.margin,
    borderRadius: position.borderRadius,
  };
}

/** @deprecated Use SKYX_NEW_CHAT_PANEL_LAYOUT[profile] */
export const SKYX_NEW_CHAT_PANEL_POSITION = {
  insetRight: SKYX_NEW_CHAT_PANEL_LAYOUT.handheldLandscape.right ?? '820px',
  insetTop: SKYX_NEW_CHAT_PANEL_LAYOUT.handheldLandscape.top ?? '-160px',
  insetBottom: SKYX_NEW_CHAT_PANEL_LAYOUT.desktop.bottom ?? '280px',
} as const;

/** @deprecated Use SKYX_NEW_CHAT_PANEL_LAYOUT[profile] */
export const SKYX_NEW_CHAT_PANEL_SIZE = {
  desktop: {
    width: SKYX_NEW_CHAT_PANEL_LAYOUT.desktop.width,
    height: 'calc(70dvh - 300px)',
  },
  tablet: {
    width: SKYX_NEW_CHAT_PANEL_LAYOUT.tablet.width,
    height: SKYX_NEW_CHAT_PANEL_LAYOUT.tablet.height,
  },
  mobile: {
    width: SKYX_NEW_CHAT_PANEL_LAYOUT.mobile.width,
    height: SKYX_NEW_CHAT_PANEL_LAYOUT.mobile.height,
  },
  handheldLandscape: {
    width: SKYX_NEW_CHAT_PANEL_LAYOUT.handheldLandscape.width,
    maxWidth: SKYX_NEW_CHAT_PANEL_LAYOUT.handheldLandscape.maxWidth,
    height: '100%',
  },
} as const;

export const skyxNewChatHeaderLayoutStyles = {
  root: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    alignItems: 'center',
    columnGap: '8px',
    minHeight: '28px',
  } satisfies React.CSSProperties,
  left: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    justifySelf: 'start',
    minWidth: 0,
  } satisfies React.CSSProperties,
  right: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    justifySelf: 'end',
  } satisfies React.CSSProperties,
  title: {
    display: 'flex',
    alignItems: 'center',
    lineHeight: '28px',
    height: '28px',
  } satisfies React.CSSProperties,
  iconButton: {
    background: 'none',
    border: 'none',
    outline: 'none',
    boxShadow: 'none',
    WebkitTapHighlightColor: 'transparent',
    appearance: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    padding: 0,
    margin: 0,
    cursor: 'pointer',
    flexShrink: 0,
    lineHeight: 1,
  } satisfies React.CSSProperties,
  closeButton: {
    fontSize: '26px',
  } satisfies React.CSSProperties,
};
